import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Shield, AlertTriangle, History, Loader2, Globe, Unlock, RotateCcw, DollarSign, ArrowRight } from 'lucide-react';

import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../contexts/FeatureContext';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Super-Admin only. Lists every registered feature with a live toggle.
 * In dev/staging `mutable` is false and the toggles are shown as
 * read-only (everything is always on). In production, flipping a toggle
 * hits `/api/admin/features/toggle` and the change takes effect within
 * a few seconds across all users (cache TTL ~15s on the backend).
 */
export default function FeatureReleaseConsole() {
  const { authToken } = useAuth();
  // Re-fetching server config after a toggle keeps the top banner in sync.
  // We don't wire directly to FeatureContext state because that's driven by
  // `/api/config/features`, not this admin endpoint.
  const { mode: contextMode } = useFeatures();

  const [data, setData] = useState(null); // { mode, mutable, features[] }
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // feature_id currently saving

  const authHeader = useMemo(
    () => ({
      Authorization: `Bearer ${authToken || 'COOKIE_SESSION'}`,
      'Content-Type': 'application/json',
    }),
    [authToken],
  );

  const fetchAll = async () => {
    try {
      const [featuresRes, auditRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/features`, {
          headers: authHeader,
          credentials: 'include',
        }),
        fetch(`${API_URL}/api/admin/features/audit`, {
          headers: authHeader,
          credentials: 'include',
        }),
      ]);
      if (!featuresRes.ok) throw new Error('Failed to load features');
      const featuresData = await featuresRes.json();
      setData(featuresData);
      if (auditRes.ok) {
        const auditData = await auditRes.json();
        setAudit(auditData);
      }
    } catch (err) {
      console.error(err);
      toast.error('Could not load feature list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pricing-preview confirmation dialog. Toggling certain feature flags
  // changes every plan's release-discount %, which silently moves the
  // effective price for every NEW signup. We intercept the toggle, fetch
  // the would-be enabled set's pricing preview, and let the admin see the
  // per-tier $ delta before committing.
  const [pendingToggle, setPendingToggle] = useState(null); // { featureId, nextEnabled, before, after }
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchPlanPricing = async (enabledIds) => {
    const res = await fetch(`${API_URL}/api/billing/plans/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled_features: enabledIds }),
    });
    if (!res.ok) throw new Error('Pricing preview failed');
    return res.json();
  };

  const requestToggle = async (featureId, nextEnabled) => {
    if (!data?.mutable) {
      toast.info(
        'Toggles only take effect in production. Dev/staging keeps every feature on.',
      );
      return;
    }
    setPreviewLoading(true);
    try {
      // Compute current enabled set + the post-toggle hypothetical set.
      const currentEnabled = (data.features || [])
        .filter((f) => f.enabled)
        .map((f) => f.id);
      const nextEnabledIds = nextEnabled
        ? Array.from(new Set([...currentEnabled, featureId]))
        : currentEnabled.filter((id) => id !== featureId);

      const [before, after] = await Promise.all([
        fetchPlanPricing(currentEnabled),
        fetchPlanPricing(nextEnabledIds),
      ]);
      setPendingToggle({ featureId, nextEnabled, before, after });
    } catch (err) {
      // If preview fails for any reason, fall back to the legacy non-preview
      // path so admins aren't blocked by a failing preview endpoint.
      console.error('Pricing preview failed, committing toggle without preview:', err);
      await handleToggle(featureId, nextEnabled);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleToggle = async (featureId, nextEnabled) => {
    if (!data?.mutable) {
      toast.info(
        'Toggles only take effect in production. Dev/staging keeps every feature on.',
      );
      return;
    }
    setSaving(featureId);
    try {
      const res = await fetch(`${API_URL}/api/admin/features/toggle`, {
        method: 'POST',
        headers: authHeader,
        credentials: 'include',
        body: JSON.stringify({ feature_id: featureId, enabled: nextEnabled }),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.detail || 'Toggle failed');
      }
      setData(body);
      toast.success(
        `${nextEnabled ? 'Enabled' : 'Disabled'} ${prettyLabel(body.features, featureId)}`,
        {
          description: 'Rollout propagates to users within ~15 seconds.',
        },
      );
      // Pull fresh audit log
      const auditRes = await fetch(`${API_URL}/api/admin/features/audit`, {
        headers: authHeader,
        credentials: 'include',
      });
      if (auditRes.ok) setAudit(await auditRes.json());
    } catch (err) {
      toast.error(err.message || 'Toggle failed');
    } finally {
      setSaving(null);
      setPendingToggle(null);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const enabledCount = data?.features?.filter((f) => f.enabled).length ?? 0;
  const total = data?.features?.length ?? 0;

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50" data-testid="feature-console-page">
      <PageHeader
        title="Feature Release Console"
        description="Control which features are live in production. Flips take effect within ~15 seconds across all users. Audited."
        badge={data?.mode?.toUpperCase()}
      />

      <div className="p-8 space-y-6 max-w-5xl">
        {/* Environment detection diagnostics (collapsible) */}
        <EnvironmentCard
          data={data}
          authHeader={authHeader}
          onChanged={fetchAll}
        />

        {/* Mode banner */}
        {!data?.mutable && (
          <div
            className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
            data-testid="mode-banner"
          >
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">You're on a {data?.mode} backend.</p>
              <p className="mt-0.5 text-amber-800">
                Every feature is forced ON in dev/staging so engineers never lose access.
                Toggles shown below are <strong>read-only previews</strong>. The switches
                become live on the production deployment at <code>fedbd.ai</code>.
                Tip: append <code>?features=prod</code> to any preview URL to simulate
                what users will see with the locked-down set. If you are on the production
                domain but still seeing this banner, use the <strong>Environment Detection</strong>
                panel above to pin this deployment to production.
              </p>
            </div>
          </div>
        )}
        {data?.mutable && (
          <div
            className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
            data-testid="mode-banner-live"
          >
            <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Live production control plane.</p>
              <p className="mt-0.5 text-emerald-800">
                Any change here affects <code>{contextMode === 'production' ? 'fedbd.ai' : 'the current production backend'}</code> for
                every user within ~15 seconds.
              </p>
            </div>
          </div>
        )}

        {/* Summary strip */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="Mode" value={data?.mode?.toUpperCase() ?? '—'} />
          <StatCard label="Enabled" value={`${enabledCount} / ${total}`} />
          <StatCard label="Audit entries" value={audit.length} />
        </div>

        {/* Feature list */}
        <Card data-testid="feature-list-card">
          <CardHeader>
            <CardTitle className="text-base">Features</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-slate-200">
              {data?.features?.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between px-6 py-4"
                  data-testid={`feature-row-${f.id}`}
                >
                  <div className="min-w-0 flex-1 pr-4">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900 truncate">{f.label}</p>
                      <Badge
                        variant={f.enabled ? 'default' : 'secondary'}
                        className={
                          f.enabled
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            : 'bg-slate-200 text-slate-600'
                        }
                      >
                        {f.enabled ? 'LIVE' : 'OFF'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 font-mono">{f.id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {(saving === f.id || (previewLoading && pendingToggle === null)) && (
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    )}
                    <Switch
                      checked={f.enabled}
                      disabled={!data.mutable || saving === f.id || previewLoading}
                      onCheckedChange={(next) => requestToggle(f.id, next)}
                      data-testid={`feature-toggle-${f.id}`}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Audit log */}
        <Card data-testid="feature-audit-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {audit.length === 0 ? (
              <p className="text-sm text-slate-500">
                No toggle activity yet. Flips made on production will appear here.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {audit.map((entry, idx) => (
                  <li
                    key={`${entry.feature_id}-${entry.at}-${idx}`}
                    className="py-3 flex items-center justify-between text-sm"
                  >
                    <div>
                      <span
                        className={
                          entry.action === 'enable'
                            ? 'text-emerald-700 font-medium'
                            : 'text-rose-700 font-medium'
                        }
                      >
                        {entry.action === 'enable' ? 'Enabled' : 'Disabled'}
                      </span>
                      <span className="ml-2 font-mono text-slate-700">{entry.feature_id}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      <span>{entry.actor_email}</span>
                      <span className="mx-2">·</span>
                      <span>{new Date(entry.at).toLocaleString()}</span>
                      <span className="mx-2">·</span>
                      <Badge variant="outline" className="text-[10px]">
                        {entry.mode}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pricing-impact confirmation dialog */}
      <PricingImpactDialog
        pendingToggle={pendingToggle}
        onCancel={() => setPendingToggle(null)}
        onConfirm={() =>
          handleToggle(pendingToggle.featureId, pendingToggle.nextEnabled)
        }
        saving={saving === pendingToggle?.featureId}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PricingImpactDialog: opens when an admin clicks a feature toggle. Renders
// a per-tier table of base, current, post-toggle effective price, and the
// $/% delta. Helps admins eyeball whether a flag flip materially shifts
// what new signups will pay.
// ---------------------------------------------------------------------------
function PricingImpactDialog({ pendingToggle, onCancel, onConfirm, saving }) {
  const open = !!pendingToggle;
  if (!open) return null;
  const { featureId, nextEnabled, before, after } = pendingToggle;
  const action = nextEnabled ? 'Enable' : 'Disable';

  // Pair each plan's before/after row by id for a single delta table.
  const rows = [];
  const allPlans = [
    ...(after.contractor_plans || []),
    ...(after.consultant_plans || []),
  ];
  const beforeById = {};
  [...(before.contractor_plans || []), ...(before.consultant_plans || [])].forEach((p) => {
    beforeById[p.id] = p;
  });
  allPlans.forEach((afterPlan) => {
    const beforePlan = beforeById[afterPlan.id] || {};
    const beforeAmount = beforePlan.effective_monthly ?? beforePlan.base_monthly ?? 0;
    const afterAmount = afterPlan.effective_monthly ?? afterPlan.base_monthly ?? 0;
    rows.push({
      id: afterPlan.id,
      base: afterPlan.base_monthly,
      isCustom: afterPlan.is_custom,
      beforeAmount,
      afterAmount,
      delta: afterAmount - beforeAmount,
      beforeDiscount: beforePlan.discount_pct || 0,
      afterDiscount: afterPlan.discount_pct || 0,
    });
  });

  const fmt = (v) =>
    v === null || v === undefined
      ? '—'
      : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const totalDelta = rows.reduce((sum, r) => (r.isCustom ? sum : sum + r.delta), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-3xl" data-testid="pricing-impact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Pricing impact preview — {action} <code className="font-mono text-sm bg-slate-100 px-2 py-0.5 rounded">{featureId}</code>
          </DialogTitle>
          <DialogDescription className="pt-1">
            Feature releases re-balance every plan's discount. New signups pay the
            <strong> AFTER</strong> price; existing subscribers stay on their locked
            rate until renewal/upgrade. Review the per-tier delta below.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-2 font-semibold">Plan</th>
                <th className="text-right p-2 font-semibold">Base</th>
                <th className="text-right p-2 font-semibold">Before</th>
                <th className="text-right p-2 font-semibold">After</th>
                <th className="text-right p-2 font-semibold">Δ / month</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`pricing-row-${r.id}`}>
                  <td className="p-2 font-mono text-xs">{r.id}</td>
                  <td className="p-2 text-right text-slate-500">
                    {r.isCustom ? <Badge variant="outline" className="text-[10px]">CUSTOM</Badge> : fmt(r.base)}
                  </td>
                  <td className="p-2 text-right">
                    {r.isCustom ? '—' : (
                      <span>
                        {fmt(r.beforeAmount)}
                        <span className="block text-[10px] text-slate-400">−{r.beforeDiscount.toFixed(1)}%</span>
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {r.isCustom ? '—' : (
                      <span>
                        {fmt(r.afterAmount)}
                        <span className="block text-[10px] text-slate-400">−{r.afterDiscount.toFixed(1)}%</span>
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right font-semibold">
                    {r.isCustom ? '—' : (
                      <span
                        className={cn(
                          'flex items-center justify-end gap-1',
                          r.delta > 0 && 'text-emerald-700',
                          r.delta < 0 && 'text-red-700',
                          r.delta === 0 && 'text-slate-400',
                        )}
                      >
                        {r.delta > 0 && <ArrowRight className="w-3 h-3 rotate-[-45deg]" />}
                        {r.delta < 0 && <ArrowRight className="w-3 h-3 rotate-45" />}
                        {r.delta > 0 ? '+' : ''}{fmt(r.delta).replace('$', '$')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-slate-200 bg-slate-50 text-xs">
              <tr>
                <td colSpan="4" className="p-2 text-right font-semibold uppercase tracking-wider text-slate-500">
                  Net Δ across non-custom tiers
                </td>
                <td className={cn(
                  'p-2 text-right font-bold',
                  totalDelta > 0 && 'text-emerald-700',
                  totalDelta < 0 && 'text-red-700',
                )}>
                  {totalDelta > 0 ? '+' : ''}{fmt(totalDelta)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {Math.abs(totalDelta) > 0 && (
          <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              <strong>Repricing reminder:</strong> existing subscribers do NOT auto-reprice.
              They stay on their current rate until they upgrade, downgrade, or their
              subscription renews. Consider grandfathering via a Stripe coupon if the delta
              is material.
            </span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="pricing-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={saving}
            className={nextEnabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
            data-testid="pricing-confirm-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Confirm — {action} feature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-xl font-semibold text-slate-900 mt-1">{value}</p>
    </div>
  );
}

/**
 * Diagnostic + manual override card. Shows the backend's detected host
 * signals and offers a "Declare Production" button when auto-detection
 * hasn't identified this deployment as production — the bulletproof
 * escape hatch for ingress configs that strip forwarding headers.
 */
function EnvironmentCard({ data, authHeader, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isProd = data?.mode === 'production';
  const isForced = !!data?.forced_mode;
  const detected = data?.detected_hosts || [];

  const callDeclare = async (mode) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/features/declare-mode`, {
        method: 'POST',
        headers: authHeader,
        credentials: 'include',
        body: JSON.stringify({ mode }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Request failed');
      toast.success(
        mode === null
          ? 'Mode override cleared — back to auto-detection.'
          : `Pinned this deployment to ${mode.toUpperCase()}.`,
      );
      onChanged();
    } catch (e) {
      toast.error(e.message || 'Could not change mode');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="env-detection-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Globe className="w-4 h-4 text-slate-500" />
          Environment Detection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Effective mode</p>
            <p
              className={
                'mt-1 font-semibold ' +
                (isProd ? 'text-emerald-700' : 'text-amber-700')
              }
            >
              {data?.mode?.toUpperCase() || '—'}
              {isForced && (
                <Badge variant="outline" className="ml-2 text-[10px]">
                  Pinned via override
                </Badge>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Backend sees these host candidates
            </p>
            {detected.length === 0 ? (
              <p className="mt-1 text-slate-500 italic">(none)</p>
            ) : (
              <ul className="mt-1 space-y-0.5">
                {detected.map((h) => (
                  <li key={h} className="font-mono text-xs text-slate-700">
                    {h}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {!isProd && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
            <p className="font-semibold mb-2">
              On the production domain but still seeing {data?.mode?.toUpperCase()}?
            </p>
            <p className="text-indigo-800 mb-3">
              Some ingress configurations strip forwarding headers so the backend
              can't auto-detect the public domain. Click below to pin this
              deployment to production — the setting persists in the database
              and overrides header detection.
            </p>
            <Button
              disabled={busy}
              onClick={() => callDeclare('production')}
              size="sm"
              data-testid="declare-production-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Unlock className="w-4 h-4 mr-1.5" />
              Pin this deployment to Production
            </Button>
          </div>
        )}

        {isForced && (
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            <div>
              <p className="font-semibold text-slate-900">Mode override active</p>
              <p className="text-slate-600">
                Currently pinned to <code>{data.forced_mode}</code> via the database.
                Auto-detection is bypassed.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => callDeclare(null)}
              data-testid="clear-mode-override-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" />
              Return to auto-detect
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function prettyLabel(features, id) {
  const match = features?.find((f) => f.id === id);
  return match ? match.label : id;
}
